import { setRegionalLanguage } from '@Common/actions/commonActions';
import { RootState } from '@Common/models/redux/store';
import { makeStyles } from '@mui/styles';
import {
  Divider,
  Grid,
  IconButton,
  Popover,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@quillbot/design-system';
import { APP_SETTINGS } from 'config/constants';
import QuillLocalStorage from 'core/utils/localStorageClient';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import CommonTrack from '@Common/handlers/track';

export interface Languages {
  [language: string]: {
    imagePath: string;
    displayText: string;
  };
}

export interface LanguageButtonProps {
  sx: SxProps<Theme>;
  setLanguageMenuOpen: Function;
  regionalLanguage: string;
  LANGUAGES: any;
  size?: { width: string | number; height: string | number };
}

export interface LanguageMeuProps {
  open: boolean;
  setLanguageMenuOpen: Function;
  handleLanguageChange: Function;
  LANGUAGES: any;
  anchorElement?: Element | null;
}

export const LANGUAGES: Languages = {
  us: {
    imagePath: '/images/American.svg',
    displayText: 'American English',
  },
  uk: {
    imagePath: '/images/British.svg',
    displayText: 'British English',
  },
  au: {
    imagePath: '/images/Australian.svg',
    displayText: 'Australian English',
  },
};

const useStyles = makeStyles(() => ({
  popoverContent: {
    pointerEvents: 'auto',
  },
  text: {
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '18px',
  },
}));

const LanguageMenu = ({
  open,
  setLanguageMenuOpen,
  handleLanguageChange,
  LANGUAGES: Languages,
  anchorElement,
}: LanguageMeuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setAnchorEl(document.getElementById('language-menu-button') as HTMLElement);
  }, []);

  const classes = useStyles();
  const theme = useTheme();

  return (
    <Popover
      sx={{
        pointerEvents: 'none',
      }}
      classes={{
        paper: classes.popoverContent,
      }}
      open={open}
      anchorEl={anchorElement || anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      disableRestoreFocus
      PaperProps={{
        onMouseEnter: () => setLanguageMenuOpen(true),
        onMouseLeave: () => setLanguageMenuOpen(false),
      }}
    >
      <Grid
        container
        flexDirection="column"
        sx={{
          width: 240,
          border: `1px solid ${theme.palette.divider}`,
          boxSizing: 'border-box',
          boxShadow: theme.shadows[5],
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Grid item sx={{ padding: '11px 16px' }}>
          <Typography className={classes.text}>I write in</Typography>
        </Grid>
        <Divider />
        {Object.keys(Languages).map((language) => (
          <>
            <Grid
              item
              sx={{
                display: 'flex',
                padding: '11px 16px',
                alignItems: 'center',
                cursor: 'pointer',
                ':hover': { background: theme.palette.background.default },
              }}
              onClick={() => handleLanguageChange(language)}
              key={language}
            >
              <img
                src={Languages[language].imagePath}
                alt="language menu"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  marginRight: 10,
                }}
              />
              <Typography className={classes.text}>{LANGUAGES[language].displayText}</Typography>
            </Grid>
            <Divider />
          </>
        ))}
      </Grid>
    </Popover>
  );
};

export function LanguageButton({
  sx,
  setLanguageMenuOpen,
  LANGUAGES: Languages,
  regionalLanguage,
  size,
}: LanguageButtonProps) {
  return (
    <IconButton
      id="language-menu-button"
      edge="end"
      aria-haspopup="true"
      onMouseEnter={() => setLanguageMenuOpen(true)}
      onMouseLeave={() => setLanguageMenuOpen(false)}
      size="large"
      style={{ padding: 0 }}
      sx={sx}
    >
      <img
        src={Languages[regionalLanguage].imagePath}
        alt="language menu"
        style={{
          width: size?.width || '30px',
          height: size?.height || '30px',
          borderRadius: '50%',
        }}
      />
    </IconButton>
  );
}

export function useLanguage() {
  const regionalLanguage = useSelector(
    (rootState: RootState) => rootState?.common.regionalLanguage
  );
  const [languageMenuOpen, setLanguageMenuOpen] = useState<boolean>(false);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setRegionalLanguage(APP_SETTINGS.REGION));
  }, []);

  const changeLanguage = (language: string) => {
    dispatch(setRegionalLanguage(language));
    setLanguageMenuOpen(false);
    QuillLocalStorage.getInstance().set('REGION', language);
    APP_SETTINGS.REGION = language;
    CommonTrack.settingChanged({ REGION: language });
  };

  return {
    regionalLanguage,
    languageMenuOpen,
    setLanguageMenuOpen,
    changeLanguage,
  };
}

export default LanguageMenu;
